import { useEffect, useState } from "react";
import { Button } from "~/components/Button";
import { FieldGroup } from "~/components/form/FieldGroup";
import { TextArea } from "~/components/form/TextArea";
import { FullPageModal, type FullPageModalProps } from "~/components/layout/modal/FullPageModal";
import { Authorize } from "~/components/permission/Authorize";
import { Goals } from "~/entities/profile/goals";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useProfile } from "~/providers/ProfileProvider";

export type GoalKey = "goal_1" | "goal_2" | "goal_3";

type GoalFormProps = {
    id: GoalKey;
    onRequestClose: () => void;
} & FullPageModalProps;

export const GoalForm = ({ id, onRequestClose, ...modalProps }: GoalFormProps) => {
    const { profile, saveGoals } = useProfile();
    const [goal, setGoal] = useState<string>(profile?.goals[id] ?? "");
    const { addToast, saving, setSaving } = useFeedback();

    const canSave = goal.trim() !== "" && !saving;
    const dirty = goal !== (profile.goals[id] ?? "");

    const doSave = () => {
        if (!canSave) {
            return;
        }

        setSaving(id);
    };

    const commit = () => {
        const payload = Goals.from({
            ...profile?.goals,
            [id]: goal,
        });

        saveGoals(payload)
            .then(() => {
                addToast({
                    icon: "checkmark-circle",
                    content: "Goal updated",
                    duration: 3000,
                });

                onRequestClose();
            })
            .finally(() => setSaving(false));
    };

    const is_saving = saving === id;

    // biome-ignore lint/correctness/useExhaustiveDependencies(commit): callback
    useEffect(() => {
        is_saving && commit();
    }, [is_saving]);

    return (
        <Authorize ability="update" section="goals" onCannot={onRequestClose}>
            <FullPageModal
                {...modalProps}
                title="Add goal"
                cancelable
                dirty={dirty}
                headerRight={() => (
                    <Button title="Save" size="sm" disabled={!canSave} onPress={doSave} loading={is_saving} />
                )}
                onRequestClose={onRequestClose}
            >
                <FieldGroup>
                    <TextArea label="Goal" value={goal} onChangeText={setGoal} disabled={is_saving} />
                </FieldGroup>
            </FullPageModal>
        </Authorize>
    );
};
